import Tooltip from '../../common/Tooltip'
import Box from '@mui/material/Box'
import { AdCreativeThumbnail } from '../../common/Thumbnail'
import { TableConfig } from '../../common/Table'
import {
  AdCreative,
  AdCreativeEvent,
  AdCreativeStatus,
  AdCreativeWithUrls,
  ListAdCreativeSortableField,
  User,
} from 'common/api/v1/types'
import { AppDispatch } from '../../../store'
import { ActionMenu } from './ActionMenu'
import Checkbox from '@mui/material/Checkbox'
import { format } from 'date-fns'
import { DATE_FORMAT_LONG, DATE_FORMAT_SHORT, formatFileSize, formatMs } from 'common/api/v1/helpers'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Link } from '../../common/Link'
import type { Routes } from '../../../utils/routes'
import { canEditAdCreative } from './List'
import { styles } from './styles'
import { adCreativeIndicateStatusText, AdCreativeIndicatorIcon } from './StatusIndicator'
import { MouseEvent } from 'react'
import { AdCreativeRHF } from '../adPod/utils'

type AdCreativeColumn = TableConfig<AdCreativeWithUrls>[number]

const status = (): AdCreativeColumn => ({
  title: 'status',
  getValue: ({ status, errorMessage }) => (
    <Tooltip title={errorMessage || adCreativeIndicateStatusText(status)} placement="top">
      <Box sx={styles.inlineWrapper}>
        <AdCreativeIndicatorIcon status={status} />
      </Box>
    </Tooltip>
  ),
  props: { width: '34px' },
})

const preview = (): AdCreativeColumn => ({
  title: 'preview',
  getValue: (adCreative) => <AdCreativeThumbnail adCreative={adCreative} />,
  props: {
    sx: { padding: 1, width: '100px' },
  },
  headerProps: {},
})

const name = (
  {
    availableFn,
    toFn,
    onClickName,
  }: {
    availableFn: (adCreative: AdCreative) => boolean
    toFn: (adCreative: AdCreative) => string
    onClickName?: (e: MouseEvent, adCreative: AdCreativeWithUrls) => void
  },
  override?: Partial<Pick<NonNullable<AdCreativeColumn>, 'props'>>,
): AdCreativeColumn => ({
  title: 'name',
  getValue: (adCreative) => (
    <Link
      available={availableFn(adCreative)}
      onClick={onClickName ? (e) => onClickName(e, adCreative) : undefined}
      to={toFn(adCreative)}
      underline="hover"
    >
      <Typography data-test-id="ad-creative-name">{adCreative.name}</Typography>
    </Link>
  ),
  sorting: {
    byParameter: ListAdCreativeSortableField.name,
  },
  props: {
    ...override?.props,
  },
})

const duration = (): AdCreativeColumn => ({
  title: 'duration',
  getValue: ({ userDurationMs, metadata }) => {
    if (userDurationMs) {
      return (
        <Tooltip title={`${userDurationMs} ms`}>
          <Typography variant="body2">{formatMs(userDurationMs)}</Typography>
        </Tooltip>
      )
    }

    if (metadata?.format?.durationMs) {
      return (
        <Tooltip title={`${metadata.format.durationMs} ms`}>
          <Typography variant="body2">{`${formatMs(metadata.format.durationMs)}`}</Typography>
        </Tooltip>
      )
    }

    return `-`
  },
  sorting: {
    byParameter: ListAdCreativeSortableField.duration,
  },
})

const mediaFormat = (): AdCreativeColumn => ({
  title: 'media format',
  getValue: ({ metadata }) => {
    const videoCodec = metadata?.video?.codec
    const audioCodec = metadata?.audio?.codec

    if (videoCodec || audioCodec) {
      return (
        <Stack direction="row" spacing={1} sx={{ padding: 1 }}>
          {videoCodec && <Chip label={videoCodec} size="small" />}
          {audioCodec && <Chip label={audioCodec} size="small" />}
        </Stack>
      )
    }

    return `-`
  },
})

const resolution = (): AdCreativeColumn => ({
  title: 'resolution',
  getValue: ({ metadata }) => {
    const width = metadata?.video?.width
    const height = metadata?.video?.height

    if (width && height) {
      return `${width}x${height}`
    }

    return `-`
  },
})

const fileSize = (): AdCreativeColumn => ({
  title: 'file Size',
  getValue: ({ metadata }) => {
    const fileSize = metadata?.format?.fileSize
    if (fileSize) {
      return (
        <Tooltip title={`${Math.round(fileSize)} bytes`}>
          <Typography variant="body2">{formatFileSize(fileSize)}</Typography>
        </Tooltip>
      )
    }

    return `-`
  },
  sorting: {
    byParameter: ListAdCreativeSortableField.fileSize,
  },
})

const filler = (): AdCreativeColumn => ({
  title: 'filler',
  getValue: ({ filler }) => {
    if (filler) {
      return (
        <Tooltip
          title={`A filler ad creative is used to fill gaps in ad pods and is not required to be tracked`}
          placement="top"
        >
          <Chip label="Filler" size="small" color="warning" />
        </Tooltip>
      )
    }

    return <></>
  },
  props: {
    align: 'left',
    width: '100px',
  },
})

const createdAt = (): AdCreativeColumn => ({
  title: 'created',
  getValue: ({ createdAt }) => (
    <Tooltip title={format(new Date(createdAt), DATE_FORMAT_LONG)} placement="top">
      <Typography variant="body2">{format(new Date(createdAt), DATE_FORMAT_SHORT)}</Typography>
    </Tooltip>
  ),
  sorting: {
    byParameter: ListAdCreativeSortableField.createdAt,
  },
})

export interface AdCreativeTableConfig {
  adCreatives: AdCreativeWithUrls[]
  dispatch: AppDispatch
  onUpload: (adCreative: AdCreativeWithUrls) => void
  selected: string[]
  handleSelect: (id: string) => void
  handleSelectAll: () => void
  user: User
  routes: Routes
}

export const getConfig = ({
  adCreatives,
  dispatch,
  onUpload,
  selected,
  handleSelect,
  handleSelectAll,
  user,
  routes,
}: AdCreativeTableConfig): AdCreativeColumn[] => [
  {
    title: (
      <Checkbox
        indeterminate={selected.length > 0 && selected.length < adCreatives.length}
        checked={selected.length === adCreatives.length}
        onChange={handleSelectAll}
        inputProps={{ 'aria-label': 'select all inputs' }}
        data-test-id={'select-all'}
      />
    ),
    getValue: ({ id }) => (
      <Checkbox
        checked={selected.includes(id)}
        onChange={() => {
          handleSelect(id)
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      />
    ),
    props: {
      padding: 'checkbox',
    },
  },
  status(),
  preview(),
  name({
    toFn: (adCreative) => routes.adCreativesUpdate({ id: adCreative.id }),
    availableFn: (adCreative) => canEditAdCreative(user, adCreative.createdBy),
    onClickName: (e, adCreative) => {
      const pendingOrOnlyUpload =
        adCreative.status === AdCreativeStatus.pending ||
        (adCreative.events.length === 1 &&
          (adCreative.events[0] === AdCreativeEvent.uploadOk || adCreative.events[0] === AdCreativeEvent.uploadFailed))

      if (pendingOrOnlyUpload) {
        onUpload(adCreative)
        e.preventDefault()
      }
    },
  }),
  duration(),
  mediaFormat(),
  resolution(),
  fileSize(),
  createdAt(),
  {
    title: '',
    getValue: (adCreative: AdCreativeWithUrls) => (
      <ActionMenu adCreative={adCreative} dispatch={dispatch} onUpload={onUpload} />
    ),
  },
]

type TransferTableColumn = TableConfig<AdCreativeRHF>[number]

interface TransferTableConfig {
  adCreatives: AdCreativeWithUrls[]
  selected: string[]
  handleSelect: (id: string) => void
  handleSelectAll: () => void
  dragable: boolean
  side: 'left' | 'right'
  user: User
  routes: Routes
}

export const getTransferTableConfig = ({
  adCreatives,
  selected,
  handleSelect,
  handleSelectAll,
  dragable,
  side,
  user,
  routes,
}: TransferTableConfig): TransferTableColumn[] =>
  [
    {
      title: (
        <Checkbox
          indeterminate={selected.length > 0 && selected.length < adCreatives.length}
          checked={selected.length === adCreatives.length}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all inputs' }}
          data-test-id={'select-all'}
        />
      ),
      getValue: ({ id, locId }) => (
        <Checkbox
          checked={selected.includes(side == 'left' ? id : locId)}
          onChange={() => {
            handleSelect(side == 'left' ? id : locId)
          }}
          onDoubleClick={(e) => e.stopPropagation()}
        />
      ),
      props: {
        padding: 'checkbox',
      },
    },
    status(),
    filler(),
    name({
      availableFn: (adCreative) => canEditAdCreative(user, adCreative.createdBy) && adCreative.status !== 'pending',
      toFn: (adCreative) => routes.adCreativesUpdate({ id: adCreative.id }),
    }),
    duration(),
    ...(dragable
      ? [
          {
            title: '',
            getValue: ({ id }: any) => (
              <Box sx={{ cursor: 'grab', display: 'flex', justifyContent: 'center' }} data-drag-handle-id={id}>
                <DragIndicatorIcon fontSize="small" />
              </Box>
            ),
            props: {
              width: '32px',
              padding: 'none',
            },
          },
        ]
      : []),
  ] as TransferTableColumn[]
